import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';

import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { getPageScrollContainer, MAIN_CONTENT_SELECTOR } from '../../../ui/utils/scroll-container';
import { useAnalyticsEvents } from '../generated/use-analytics-events';
import { type AuthState, SCROLL_DEPTH_BUCKETS_IN_ORDER, type ScrollDepthBucket } from '../types';

const BUCKET_PERCENTAGE: Record<ScrollDepthBucket, number> = {
	'25': 25,
	'50': 50,
	'75': 75,
	'100': 100,
};

const SCROLL_THROTTLE_MS = 200;

type ScrollMetrics = {
	scrollTop: number;
	viewport: number;
	scrollHeight: number;
};

const readMetrics = (container: HTMLElement | null): ScrollMetrics | null => {
	if (container) {
		return {
			scrollTop: container.scrollTop,
			viewport: container.clientHeight,
			scrollHeight: container.scrollHeight,
		};
	}
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!doc || !win) {
		return null;
	}
	return {
		scrollTop: win.scrollY ?? doc.documentElement.scrollTop ?? doc.body.scrollTop ?? 0,
		viewport: win.innerHeight ?? doc.documentElement.clientHeight ?? 0,
		scrollHeight: doc.documentElement.scrollHeight ?? doc.body.scrollHeight ?? 0,
	};
};

const computeDepthPercent = (container: HTMLElement | null): number => {
	const metrics = readMetrics(container);
	if (!metrics) {
		return 0;
	}
	const scrollable = Math.max(0, metrics.scrollHeight - metrics.viewport);
	if (scrollable === 0) {
		// Content fits in the viewport: anything visible is effectively the
		// top of the page. Reporting `100` here would inflate the metric, so
		// treat it as "saw the top" only.
		return 0;
	}
	return Math.min(100, Math.max(0, (metrics.scrollTop / scrollable) * 100));
};

export type UseScrollDepthEventOptions = {
	/**
	 * Which page's scroll-depth event to fire. Defaults to
	 * `'twgMicrositeHome'` so the existing home call site keeps working
	 * unchanged. The generated `fireEvent` keys are distinct string
	 * literals per actionSubjectId, so the hook branches on this
	 * literal internally rather than interpolating it into the key.
	 */
	actionSubjectId?: 'twgMicrositeHome' | 'twgMicrositeCLIPage' | 'twgMicrositeSetupPage';
};

/**
 * Listens for scroll events on whichever element actually scrolls the
 * page (ADS's `<Main>` on desktop, the document on mobile) and fires
 * `ui.scrollDepth.reached.twgMicrositeHome` (or the CLI-page
 * equivalent) exactly once per bucket (25 / 50 / 75 / 100) per page
 * load.
 *
 * Cumulative semantics: scrolling from 0% to 80% in one motion fires
 * 25, 50 AND 75. Scrolling back up does not fire anything.
 *
 * The listener is throttled to a maximum of one fire per
 * `SCROLL_THROTTLE_MS` and disposed once all four buckets have been
 * reached so the hook has zero ongoing cost on heavy pages.
 *
 * Mount this once per page (e.g. from the home `<Page>` shell, or the
 * CLI page with `{ actionSubjectId: 'twgMicrositeCLIPage' }`). It is
 * a no-op outside the browser (SSR, tests without `window`).
 */
export const useScrollDepthEvent = ({
	actionSubjectId = 'twgMicrositeHome',
}: UseScrollDepthEventOptions = {}): void => {
	const { fireEvent } = useAnalyticsEvents();
	const { isAuthed } = useAtlassianUserContext();
	const authState: AuthState = isAuthed ? 'loggedIn' : 'loggedOut';

	const firedBucketsRef = useRef<Set<ScrollDepthBucket>>(new Set());
	const authStateRef = useRef(authState);
	useEffect(() => {
		authStateRef.current = authState;
	}, [authState]);

	useEffect(() => {
		const doc = getDocument();
		const win = doc?.defaultView;
		if (!doc || !win) {
			return;
		}

		let container = getPageScrollContainer();
		let throttleHandle: ReturnType<typeof setTimeout> | null = null;
		let pending = false;
		let unbindScroll: (() => void) | null = null;

		/**
		 * Whether a captured scroll event came from the page scroller, adopting it
		 * as the container when it only became identifiable after layout settled.
		 *
		 * Capture also hands us every inner widget's scrolling, and the CLI terminal
		 * transcript scrolls on every beat of its replay. Measuring forces a layout
		 * via `scrollHeight`, so those are filtered out. An element that just
		 * scrolled already has a scroll range and a scrollable overflow, so a target
		 * that is the main landmark, or an ancestor of it, is the page scroller.
		 */
		const isPageScroll = (target: EventTarget | null): boolean => {
			if (container) {
				return target === container;
			}
			if (
				target === doc ||
				target === win ||
				target === doc.documentElement ||
				target === doc.body
			) {
				return true;
			}
			if (!(target instanceof win.HTMLElement)) {
				return false;
			}
			const main = doc.querySelector<HTMLElement>(MAIN_CONTENT_SELECTOR);
			if (main && (target === main || target.contains(main))) {
				container = target;
				return true;
			}
			return false;
		};

		const onScroll = (): void => {
			if (throttleHandle !== null) {
				pending = true;
				return;
			}
			throttleHandle = setTimeout(() => {
				throttleHandle = null;
				if (pending) {
					pending = false;
					onScroll();
				}
			}, SCROLL_THROTTLE_MS);
			const depthPercent = computeDepthPercent(container);
			for (const bucket of SCROLL_DEPTH_BUCKETS_IN_ORDER) {
				if (firedBucketsRef.current.has(bucket)) {
					continue;
				}
				if (depthPercent >= BUCKET_PERCENTAGE[bucket]) {
					firedBucketsRef.current.add(bucket);
					if (actionSubjectId === 'twgMicrositeCLIPage') {
						fireEvent('ui.scrollDepth.reached.twgMicrositeCLIPage', {
							depthBucket: bucket,
							authState: authStateRef.current,
						});
					} else if (actionSubjectId === 'twgMicrositeSetupPage') {
						fireEvent('ui.scrollDepth.reached.twgMicrositeSetupPage', {
							depthBucket: bucket,
							authState: authStateRef.current,
						});
					} else {
						fireEvent('ui.scrollDepth.reached.twgMicrositeHome', {
							depthBucket: bucket,
							authState: authStateRef.current,
						});
					}
				}
			}
			if (firedBucketsRef.current.size === SCROLL_DEPTH_BUCKETS_IN_ORDER.length && unbindScroll) {
				unbindScroll();
				unbindScroll = null;
			}
		};

		// Scroll events do not bubble, so the window fallback captures: that catches
		// both the document scrolling and a container that only became identifiable
		// after layout settled. `isPageScroll` sorts those from everything else.
		unbindScroll = container
			? bind(container, { type: 'scroll', listener: onScroll, options: { passive: true } })
			: bind(win, {
					type: 'scroll',
					listener: (event) => {
						if (isPageScroll(event.target)) {
							onScroll();
						}
					},
					options: { passive: true, capture: true },
				});
		// Fire-once on mount in case the page is already scrolled. Reads the document
		// unless `<Main>` has a scroll range by now, so a container offset restored
		// on reload is picked up by the first scroll event rather than here.
		onScroll();
		return (): void => {
			if (unbindScroll) {
				unbindScroll();
			}
			if (throttleHandle !== null) {
				clearTimeout(throttleHandle);
			}
		};
	}, [fireEvent, actionSubjectId]);
};
